import React, { useState, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";
import api from "./services/api"; // Import the API service

// Define types for the video job data
interface VideoJobLog {
  timestamp: string;
  message: string;
}

interface VideoJob {
  id: string;
  project_id: string;
  status: string;
  current_step: string | null;
  progress: number;
  error_message: string | null;
  logs: VideoJobLog[];
  result_video_url: string | null;
  talking_face_url: string | null;
  created_at: string;
  updated_at: string;
  user_email: string;
  user_videos_generated: number;
}

const AdminJobsPage: React.FC = () => {
  const [jobs, setJobs] = useState<VideoJob[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        // Use the API service instead of direct axios call
        const response = await api.get("/api/videos/admin/jobs");
        setJobs(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching jobs:", err);
        setError("Failed to load video jobs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
    // Refresh data every 30 seconds
    const interval = setInterval(fetchJobs, 30000);
    return () => clearInterval(interval);
  }, []);

  const toggleExpandJob = (jobId: string) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
    } else {
      setExpandedJobId(jobId);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "failed":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "processing":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "queued":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 dark:border-indigo-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded relative m-4 transition-colors duration-200">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Admin Video Jobs Dashboard</h1>

      <div className="mb-4">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 transition-colors duration-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              All Video Jobs ({jobs.length})
            </h2>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Last updated: {new Date().toLocaleTimeString()}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Job ID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    User
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Progress
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Created
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {jobs.map((job) => (
                  <React.Fragment key={job.id}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {job.id.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <span
                          className="cursor-pointer relative group flex items-center"
                          title={`Total videos generated: ${job.user_videos_generated}`}
                        >
                          {job.user_email}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 ml-1 text-gray-400 dark:text-gray-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black text-white dark:bg-gray-700 text-xs py-1 px-2 rounded whitespace-nowrap z-10">
                            Videos generated: {job.user_videos_generated}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                            job.status
                          )}`}
                        >
                          {job.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                          <div
                            className="bg-blue-600 dark:bg-blue-500 h-2.5 rounded-full"
                            style={{ width: `${job.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs mt-1">{job.progress}%</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDistanceToNow(new Date(job.created_at), {
                          addSuffix: true,
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => toggleExpandJob(job.id)}
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors duration-150"
                        >
                          {expandedJobId === job.id ? "Hide Logs" : "View Logs"}
                        </button>
                      </td>
                    </tr>
                    {expandedJobId === job.id && (
                      <tr>
                        <td colSpan={6} className="px-6 py-4">
                          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg transition-colors duration-200">
                            <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                              Job Details
                            </h3>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Project ID
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {job.project_id}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Current Step
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {job.current_step || "N/A"}
                                </p>
                              </div>
                              {job.result_video_url && (
                                <div>
                                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Result Video
                                  </p>
                                  <a
                                    href={job.result_video_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-500 hover:underline dark:text-blue-400"
                                  >
                                    View Video
                                  </a>
                                </div>
                              )}
                              {job.talking_face_url && (
                                <div>
                                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Talking Face Video
                                  </p>
                                  <a
                                    href={job.talking_face_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-500 hover:underline dark:text-blue-400"
                                  >
                                    View Talking Face
                                  </a>
                                </div>
                              )}
                            </div>

                            <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                              Logs
                            </h3>
                            <div className="bg-black dark:bg-gray-900 rounded-lg p-3 text-xs text-white font-mono h-64 overflow-y-auto">
                              {job.logs.map((log, index) => (
                                <div key={index} className="mb-1">
                                  <span className="text-gray-400">
                                    [{new Date(log.timestamp).toLocaleString()}]
                                  </span>{" "}
                                  {log.message}
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminJobsPage;
